import React, { useState } from 'react';
import 'twin.macro';
import {
  usePayByBankTransferModal,
  type BankTransferType,
} from './usePayByBankTransferModal';
import { useTranslation } from 'react-i18next';
import Dialog from 'components/Dialog';
import { Radios } from 'components/Form/Radios';
import Button from 'components/Buttons/Button';
import { Totaux } from 'containers/Billing/Totaux';
import { TransferPayment } from './TransferPayment';
import { MandatePayment } from './MandatePayment';
import { useCreatePaymentIntent } from 'queries/payments/useCreatePaymentIntent';
import { usePendingBankTransferModal } from '../PendingBankTransferModal/usePendingBankTransferModal';

export const TRANSFER_DETAILS = {
  email: 'ecoles@teetsh.com',
  accountHolder: 'TEETSH',
  iban: 'FR76 1695 8000 0188 2834 3154 290',
  bic: 'QNTOFRP1XXX',
};

export function PayByBankTransferModal() {
  const { isOpen, closeModal, payload, setType } = usePayByBankTransferModal();
  const { t } = useTranslation(['common', 'userSettings']);
  const { mutateAsync: initPayment, isPending } = useCreatePaymentIntent();
  const { open: openPendingModal } = usePendingBankTransferModal();

  const [selectedType, setSelectedType] =
    useState<BankTransferType>('transfer');

  const step = !payload?.type ? 'select' : 'instructions';

  const { title, cancelTitle, validateTitle, validateAction } = (() => {
    switch (payload?.type) {
      case 'transfer':
        return {
          title: t(
            'userSettings:schoolSubscription.payByBankTransfer.transfer.title',
          ),
          cancelTitle: t('common:buttons.close'),
          validateTitle: t('common:buttons.next'),
          validateAction: async () => {
            if (!payload?.cart) return;
            await initPayment({
              products: {
                essentiel: payload.cart.essentielQuantity,
                integral: payload.cart.integralQuantity,
              },
              paymentMethod: 'bank_transfer',
            });

            closeModal();
            openPendingModal();
          },
        };
      case 'mandate':
        return {
          title: t(
            'userSettings:schoolSubscription.payByBankTransfer.mandate.title',
          ),
          cancelTitle: t('common:buttons.close'),
          validateTitle: t(
            'userSettings:schoolSubscription.payByBankTransfer.transfer.sendEmail',
          ),
          validateAction: async () => {
            if (!payload?.cart) return;
            await initPayment({
              products: {
                essentiel: payload.cart.essentielQuantity,
                integral: payload.cart.integralQuantity,
              },
              paymentMethod: 'administrative_order',
            });

            window.open(`mailto:${TRANSFER_DETAILS.email}`, '_blank');
            closeModal();
          },
        };
      case undefined:
      default:
        return {
          title: t(
            'userSettings:schoolSubscription.payByBankTransfer.selectType.title',
          ),
          cancelTitle: t('common:buttons.cancel'),
          validateTitle: t('common:buttons.next'),
          validateAction: () => setType(selectedType || 'transfer'),
        };
    }
  })();

  return (
    <Dialog size="lg" isOpen={isOpen} onClose={closeModal} title={title}>
      {payload && (
        <>
          <Dialog.Body>
            {step === 'select' && (
              <div tw="space-y-6">
                <span tw="text-sm text-neutral-intense">
                  {t(
                    'userSettings:schoolSubscription.payByBankTransfer.selectType.label',
                  )}
                </span>
                <Radios<BankTransferType>
                  options={[
                    {
                      value: 'transfer',
                      label: t(
                        'userSettings:schoolSubscription.payByBankTransfer.selectType.transfer',
                      ),
                    },
                    {
                      value: 'mandate',
                      label: t(
                        'userSettings:schoolSubscription.payByBankTransfer.selectType.mandate',
                      ),
                    },
                  ]}
                  value={selectedType}
                  onChange={setSelectedType}
                />
                <div tw="text-neutral-moderate text-sm">
                  <span tw="font-semibold">
                    {t(
                      'userSettings:schoolSubscription.payByBankTransfer.selectType.helpText',
                    )}{' '}
                  </span>
                  {t(
                    'userSettings:schoolSubscription.payByBankTransfer.selectType.contactUs',
                  )}{' '}
                  <a
                    href={`mailto:${
                      TRANSFER_DETAILS.email
                    }?subject=${encodeURIComponent(
                      t(
                        'userSettings:schoolSubscription.payByBankTransfer.emailSubject',
                      ),
                    )}`}
                    target="_blank"
                  >
                    {TRANSFER_DETAILS.email}
                  </a>
                  .
                </div>
              </div>
            )}
            {step === 'instructions' && (
              <div>
                <h3 tw="mb-4 font-semibold text-base">
                  {t(
                    'userSettings:schoolSubscription.payByBankTransfer.transfer.orderSummary',
                  )}
                </h3>
                <div tw="p-4 bg-white rounded-lg">
                  <Totaux
                    cart={payload.cart}
                    plans={payload.plans}
                    showMissingAboToReachNextPromotion={false}
                  />
                </div>
                <h3 tw="mt-6 mb-4 font-semibold text-base">
                  {t(
                    'userSettings:schoolSubscription.payByBankTransfer.transfer.paymentInstructions',
                  )}
                </h3>
                {payload.type === 'transfer' && <TransferPayment />}
                {payload.type === 'mandate' && <MandatePayment />}
              </div>
            )}
          </Dialog.Body>
          <Dialog.Footer>
            <Button onClick={closeModal} disabled={isPending}>
              {cancelTitle}
            </Button>
            <Button
              intent="primary"
              onClick={validateAction}
              loading={isPending}
            >
              {validateTitle}
            </Button>
          </Dialog.Footer>
        </>
      )}
    </Dialog>
  );
}
